import { DatePipe, JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { PreferencesService } from '@app/preferences/service/preferences-service';
import {
  DAY_TOTAL_MARGIN,
  daysInMs,
  LAST_INFO_MODAL_SHOWN_KEY,
  MD_BREAKPOINT,
} from '@app/shared/constants';
import { WorkLocation } from '@app/shared/enum/WorkLocation';
import { TruncateTitlePipe } from '@app/shared/pipes/truncate-title-pipe';
import { getDaysInMonth, getWeeksInMonthDict, WeekDict } from '@app/shared/util/date-utils';
import { formatAndValidateTime } from '@app/shared/util/time-utils';
import { AuthService } from '@auth/auth-service';
import { MonthYearSelector } from '@components/month-year-selector/month-year-selector';
import { WeekDays } from '@enum/week-days';
import { ENVIRONMENT } from '@env/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap/tooltip';

import { getISODay, getISOWeek, isWeekend } from 'date-fns';
import { ToastrService } from 'ngx-toastr';

import { DownloadButton } from './components/download-button/download-button';
import { TimeSheetActionButtons } from './components/time-sheet-action-buttons/time-sheet-action-buttons';
import { TimeSheetExtraInfoCard } from './components/time-sheet-extra-info-card/time-sheet-extra-info-card';
import { TimesheetInfoModal } from './components/timesheet-info-modal/timesheet-info-modal';
import { TIMESHEET_MODAL_DATA } from './components/timesheet-info-modal/timesheet-modal-data.token';
import { Day } from './models/day';
import { SelectedCell } from './models/selected-cell';
import { TimeSheetTask } from './models/time-sheet-task';
import { TimeSheetUpdate, TimeSheetUpdateDto } from './models/time-sheet-update';
import { TimesheetSavePayload } from './models/timesheet-save-payload';
import { TimeSheetChangeTrackerService } from './service/time-sheet-change-tracker-service';
import { TimeSheetService } from './service/time-sheet-service';
import { isTaskVisibleOnDay } from './util/time-sheet-date-utils';
import { sumTimes, timeToTimeInputFormat } from './util/time-sheet-utils';

@Component({
  selector: 'abc-time-sheet',
  imports: [
    MonthYearSelector,
    JsonPipe,
    TimeSheetActionButtons,
    TimeSheetExtraInfoCard,
    DownloadButton,
    DatePipe,
    TruncateTitlePipe,
    NgbTooltipModule,
  ],
  templateUrl: 'time-sheet.html',
  styleUrl: 'time-sheet.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSheet {
  protected isWeekend = isWeekend;
  protected weeklyTotalHeaderTitle = $localize`:header|Column header for weekly totals in the timesheet table
                footer@@weeklyHoursHeader:Weekly total`;
  protected dailyTotalHeaderTitle = $localize`:header|Column header for daily total hours in the timesheet
                table@@dailyTotalHoursHeader:Daily total`;

  // DEPENDENCIES
  private authService = inject(AuthService);
  private timeSheetService = inject(TimeSheetService);
  private changeTrackerService = inject(TimeSheetChangeTrackerService);
  private preferencesService = inject(PreferencesService);
  private activatedRoute = inject(ActivatedRoute);
  private toastrService = inject(ToastrService);
  private modalService = inject(NgbModal);
  private injector = inject(Injector);

  // SIGNALS
  protected showDebugtools = ENVIRONMENT.showDebugtools;

  selectedYear = signal<number>(new Date().getFullYear());
  selectedMonth = signal<number>(new Date().getMonth() + 1);

  isHeaderCollapsed = signal<boolean>(false);

  daysOfMonth = computed<Date[]>(() => getDaysInMonth(this.selectedMonth(), this.selectedYear()));

  weeks = computed(() => getWeeksInMonthDict(this.selectedMonth(), this.selectedYear()));

  weekFooterCells = computed(() => {
    const days = this.daysOfMonth();
    const totalDays = days.length;
    const cells: { content: TableFooterCell; span: number }[] = [];

    let i = 0;
    while (i < totalDays) {
      const startDay = days[i];
      const weekNum = getISOWeek(startDay);

      // Count consecutive days in the same ISO week
      let span = 1;
      let j = i + 1;
      while (j < totalDays && getISOWeek(days[j]) === weekNum) {
        span++;
        j++;
      }

      // Only compute total once per week
      const weekDays = days.slice(i, i + span);
      const total = this.getWeekTotal({ daysInWeek: weekDays, weekNum });

      cells.push({ content: total, span });

      i += span;
    }

    return cells;
  });

  pendingChanges = this.changeTrackerService.changes;
  hasPendingChanges = this.changeTrackerService.pendingChanges;

  selectedCell = signal<SelectedCell | null>(null);
  timeSheetData = signal<TimeSheetTask[] | null>(null);
  hasTimeSheetData = computed(() => !!this.timeSheetData());

  constructor() {
    this.activatedRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const year = Number(params.get('year'));
      const month = Number(params.get('month'));

      if (!isNaN(year) && year > 1900 && year < 2200) {
        this.selectedYear.set(year);
      }

      if (!isNaN(month) && month >= 1 && month <= 12) {
        this.selectedMonth.set(month);
      }
    });

    effect(() => {
      this.timeSheetData.set(null);
      this.selectedCell.set(null);
      this.timeSheetService.refreshTimeSheetData(this.selectedYear(), this.selectedMonth());
    });

    this.timeSheetService.timeSheetData$
      .pipe(takeUntilDestroyed())
      .subscribe((data: TimeSheetTask[]) => {
        this.changeTrackerService.reset();
        this.timeSheetData.set(data);
      });

    if (window.innerWidth < MD_BREAKPOINT) {
      this.isHeaderCollapsed.set(true);
    }

    this.checkAndShowInfoModal();
  }

  private checkAndShowInfoModal() {
    // Dont show absences info modal to freelancers
    if (this.authService.isFreelancer()) {
      return;
    }

    const lastShownStr = localStorage.getItem(LAST_INFO_MODAL_SHOWN_KEY);
    const now = Date.now();

    const injector = Injector.create({
      providers: [
        {
          provide: TIMESHEET_MODAL_DATA,
          useValue: { year: this.selectedYear(), month: this.selectedMonth() },
        },
      ],
      parent: this.injector,
    });

    if (!lastShownStr) {
      this.modalService.open(TimesheetInfoModal, { injector });

      return;
    }

    const lastShown = parseInt(lastShownStr, 10);
    if (now - lastShown >= daysInMs(5)) {
      this.modalService.open(TimesheetInfoModal, { injector });
    }
  }

  // ACTIONS
  protected saveTimesheet() {
    const pendingChanges = this.pendingChanges();
    const payload: TimesheetSavePayload = {
      year: this.selectedYear(),
      month: this.selectedMonth(),
      entries: pendingChanges.map((change) => {
        const { title: _title, ...otherFields } = change;
        return otherFields as TimeSheetUpdateDto;
      }),
    };

    if (payload.entries.length === 0) {
      this.toastrService.warning(
        $localize`:toast|Warning message for save timesheet with no changes@@timesheetNoChanges:The Timesheet has no changes to save.`
      );
      return;
    }
    this.clearSelectedDay();

    this.timeSheetService.savetimeSheetDate(payload).subscribe({
      next: () =>
        this.toastrService.success(
          $localize`:toast|Success message for saved timesheet@@timesheetSuccess:Timesheet saved successfully.`
        ),
      error: () =>
        this.toastrService.error(
          $localize`:toast|Error message for failed timesheet save@@timesheetError:Failed to save timesheet.`
        ),
    });
  }

  // EVENT HANDLERS
  protected onTimeChange(task: TimeSheetTask, day: Date, event: Event) {
    const input = event.target as HTMLInputElement;
    const formatted = formatAndValidateTime(input.value);

    const dayOfMonth = day.getDate();
    const serverDays = this.getServerEntries(task, day);
    const serverEntry = this.mergeServerDays(serverDays, task, dayOfMonth);

    if (formatted) {
      input.value = formatted;
      this.changeTrackerService.updateChange(task, day, { totalHours: formatted }, serverEntry);
    } else {
      input.value = '';
      this.changeTrackerService.updateChange(task, day, { totalHours: null }, serverEntry);
    }
  }

  private mergeServerDays(
    serverDays: Day[],
    task: TimeSheetTask,
    dayOfMonth: number
  ): TimeSheetUpdate | null {
    if (serverDays.length === 0) return null;

    const first = serverDays[0];
    const totalHours = sumTimes(serverDays.map((d) => d.totalHours));
    const descriptions = serverDays
      .map((d) => d.description)
      .filter((d) => d != null && d.trim() !== '');
    const uniqueDescriptions = [...new Set(descriptions)];
    const description = uniqueDescriptions.join('; ');
    const startTimes = serverDays.map((d) => d.startTime).filter((s) => s);
    const startTime = startTimes.length > 0 ? startTimes.sort()[0] : first.startTime;
    const lunchLengths = serverDays.map((d) => d.lunchLength || '00:00');
    const lunchLength = sumTimes(lunchLengths);
    const locations = serverDays.map((d) => d.workLocation);
    const firstLocation = locations[0];
    const workLocation = locations.every((l) => l === firstLocation)
      ? firstLocation
      : WorkLocation.Mixed;

    return {
      projectId: task.projectId,
      title: `${task.customerName} - ${task.taskName}`,
      customerName: task.customerName,
      taskName: task.taskName,
      dayOfMonth,
      totalHours,
      startTime,
      lunchLength,
      description,
      workLocation,
    };
  }

  protected onCellFocus(task: TimeSheetTask, day: Date) {
    this.selectedCell.set({ task, date: day.toDateString() });
  }

  protected handleReadonlyClick(input: HTMLInputElement, task: TimeSheetTask, day: Date) {
    input.focus();
    this.onCellFocus(task, day);
  }

  protected getCellClass(task: TimeSheetTask, day: Date): string {
    if (this.getCellReadonly(task)) return 'disabled-look';
    if (this.hasUnsavedChanges(task, day)) return 'bg-primary-subtle';
    if (this.isSelectedCell(task, day)) return 'bg-info-subtle';
    if (isWeekend(day)) return 'bg-secondary-subtle';
    return '';
  }

  private getServerTotalHours(task: TimeSheetTask, day: Date): string {
    // const data = this.timeSheetData();
    // if (!data) return '';

    // const foundTask = data.find((t) => t.projectId === task.projectId);
    // if (!foundTask?.days) return '';

    // const matchingDays = foundTask.days.filter((entry) => {
    //   const entryDate = new Date(this.selectedYear(), this.selectedMonth() - 1, entry.dayOfMonth);
    //   return entryDate.toDateString() === day.toDateString();
    // });
    const matchingDays = this.getServerEntries(task, day);

    if (matchingDays.length === 0) return '';

    const times = matchingDays.map((d) => d.totalHours);
    return sumTimes(times);
  }

  getTimeValue(task: TimeSheetTask, day: Date): string {
    const tracked = this.changeTrackerService.getChange(task, day);
    if (tracked) return tracked.totalHours || '';

    const serverTotal = this.getServerTotalHours(task, day);
    if (serverTotal) return serverTotal;

    return '';
  }

  private getServerEntries(task: TimeSheetTask, day: Date): Day[] {
    const data = this.timeSheetData();
    if (!data) return [];

    const foundTask = data.find((t) => t.projectId === task.projectId);
    if (!foundTask?.days) return [];

    return foundTask.days.filter((entry) => {
      const entryDate = new Date(this.selectedYear(), this.selectedMonth() - 1, entry.dayOfMonth);
      return entryDate.toDateString() === day.toDateString();
    });
  }

  // LAYOUT FUNCTIONS
  private isSelectedCell(task: TimeSheetTask, day: Date): boolean {
    const selected = this.selectedCell();
    return (
      !!selected &&
      selected.task.projectId === task.projectId &&
      selected.date === day.toDateString()
    );
  }

  private hasUnsavedChanges(task: TimeSheetTask, day: Date): boolean {
    return this.changeTrackerService.hasChanges(task, day);
  }

  shouldShowTimeInput(task: TimeSheetTask, day: Date): boolean {
    return isTaskVisibleOnDay(task, day);
  }

  getDayTotal(day: Date): TableFooterCell | null {
    const data = this.timeSheetData();
    if (!data) return null;

    let totalMinutes = 0;
    for (const task of data) {
      if (!this.shouldShowTimeInput(task, day)) continue;

      const timeValue = this.getTimeValue(task, day);
      if (timeValue) {
        const [hours, minutes] = timeValue.split(':').map(Number);
        totalMinutes += hours * 60 + minutes;
      }
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const [expectedHours, expectedMins] = this.getHoursPreferenceForDay(day) ?? [8, 0];
    const expectedMinutes = expectedHours * 60 + expectedMins;

    return {
      time: totalMinutes === 0 ? '-' : timeToTimeInputFormat(hours, minutes),
      flagged:
        totalMinutes !== 0 &&
        (totalMinutes > expectedMinutes + DAY_TOTAL_MARGIN ||
          totalMinutes < expectedMinutes - DAY_TOTAL_MARGIN),
      notFilledIn: totalMinutes === 0 && !isWeekend(day),
    };
  }

  private getHoursPreferenceForDay(day: Date): number[] | null {
    const prefs = this.preferencesService.preferences();
    if (!prefs) return null;

    const isoDay = getISODay(day);
    const isoDayMap: Record<number, WeekDays | null> = {
      1: WeekDays.Monday,
      2: WeekDays.Tuesday,
      3: WeekDays.Wednesday,
      4: WeekDays.Thursday,
      5: WeekDays.Friday,
      6: null,
      7: null,
    };

    const weekDay = isoDayMap[isoDay];
    if (weekDay) {
      const dayPref = prefs[weekDay];
      if (dayPref?.hours) {
        return dayPref.hours.split(':').map(Number);
      } else {
        return [0, 0];
      }
    }

    return null;
  }

  protected toggleCollapsed() {
    this.isHeaderCollapsed.set(!this.isHeaderCollapsed());
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const target = event.target as Window;
    if (target.innerWidth >= MD_BREAKPOINT) {
      this.isHeaderCollapsed.set(false);
    }
  }

  getWeekTotal(week: WeekDict): TableFooterCell {
    let totalMinutes = 0;
    let flagged = false;
    let notFilledIn = false;

    for (const day of week.daysInWeek) {
      const dayTotal = this.getDayTotal(day);

      if (dayTotal) {
        if (dayTotal.flagged) flagged = true;
        if (dayTotal.notFilledIn) notFilledIn = true;

        if (dayTotal.time && dayTotal.time !== '-') {
          const [hours, minutes] = dayTotal.time.split(':').map(Number);
          totalMinutes += hours * 60 + minutes;
        }
      }
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      time: totalMinutes === 0 ? '-' : timeToTimeInputFormat(hours, minutes),
      flagged: flagged,
      notFilledIn: notFilledIn,
    };
  }

  // is used for absence and sickness tasks
  // freelancers dont have access to "verlofapp" so they should be able to fill it in here.
  // power user impersonation should also be able to "fix" mistakes
  getCellReadonly(task: TimeSheetTask): boolean {
    if (task.completed) return true;

    if (this.authService.isFreelancer() || this.authService.isImpersonating()) {
      return false;
    }

    return task.readOnly;
  }

  getCellTitle(task: TimeSheetTask, day: Date): string {
    const tracked = this.changeTrackerService.getChange(task, day);
    const serverEntries = this.getServerEntries(task, day);

    const serverDescriptions = serverEntries
      .map((entry) => entry.description)
      .filter((desc) => desc != null && desc.trim() !== '');

    const mergedServerDescription =
      serverDescriptions.length > 0 ? serverDescriptions.join('\n') : null;

    const description = tracked?.description ?? mergedServerDescription;
    return description || '';
  }

  clearSelectedDay() {
    this.selectedCell.set(null);
  }
}

interface TableFooterCell {
  time: string;
  flagged: boolean;
  notFilledIn: boolean;
}
